import clsx from 'clsx';
import React from 'react';

import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { OfferOption } from '@utils/backend-api/subscribe';
import { formatNumber } from '@utils/formatting';
import getSubscriptionPriceBreakdown from '@utils/getSubscriptionPriceBreakdown';
import { renewCancelText } from '@utils/offerHelper';

import SubscribeForm from './SubscribeForm';

const NAVBAR_HEIGHT = 60;

interface VerticalOfferProps {
  className?: string;
  isOfferSelected: boolean;
  isMobile: boolean;
  offer: OfferOption;
  presentedOffers: OfferOption[];
  setSelectedOffer: (offer: OfferOption) => void;
}

const VerticalOffer = ({
  className,
  isOfferSelected,
  isMobile,
  offer,
  presentedOffers,
  setSelectedOffer,
}: VerticalOfferProps) => {
  const {
    code,
    title,
    isPro,
    currentPriceDisplayOverride,
    currentPrice,
    originalPrice,
    originalPriceDisplayOverride,
    billingFrequencyOverride,
    description,
  } = offer;
  const { currentPriceToDisplay, hasDiscount, originalPriceToDisplay } =
    getSubscriptionPriceBreakdown({
      currentPrice,
      currentPriceDisplayOverride,
      originalPrice,
      originalPriceDisplayOverride,
    });
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.VerticalOffer,
  });

  const handleSelectedOffer = () => {
    setSelectedOffer(offer);
    if (isMobile) {
      const element = document.getElementById(code);
      if (!element) return;
      const y = element.getBoundingClientRect().top + window.scrollY - NAVBAR_HEIGHT;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    trackEvent(click({ clickTarget: ClickTarget.Offer, offerCode: code }));
  };

  return (
    <div
      id={code}
      className={clsx(
        className,
        `relative w-full touch-manipulation border border-t-0 px-24 py-32 sm:w-1/2 sm:max-w-full md:px-48 md:py-48`,
        {
          'max-h-full cursor-default border-0 border-b-0 border-l-0 border-r-0 bg-grayscale-beige':
            isOfferSelected,
          'max-h-[480px] cursor-pointer bg-white opacity-50': !isOfferSelected,
        }
      )}
      onClick={handleSelectedOffer}
    >
      <div className="mb-24 md:mb-32">
        <div className="flex flex-col-reverse items-start sm:flex-row sm:items-center">
          <h2
            className={clsx(
              'm-0 font-suisse-works text-headline-xs text-black md:text-headline-sm',
              {
                'text-primary-magenta': !isPro,
                'text-pro-gold': isPro,
              }
            )}
          >
            {title}
          </h2>
        </div>
        <div className="mt-8 flex flex-wrap items-center">
          <span className="font-suisse-screen text-headline-alt-sm font-bold text-black">
            ${formatNumber(currentPriceToDisplay)}
          </span>
          {hasDiscount && (
            <span className="ml-8 font-suisse-screen text-headline-alt-sm text-grayscale-gray line-through sm:ml-8">
              ${formatNumber(originalPriceToDisplay)}
            </span>
          )}
          {billingFrequencyOverride && (
            <span className="ml-8 font-suisse-screen text-disclosure-lg text-grayscale-charcoal sm:ml-8">
              {billingFrequencyOverride}
            </span>
          )}
        </div>
        <div className="mt-8 min-h-72 font-suisse-intl  text-body-md text-grayscale-gray md:min-h-56 md:text-body-lg">
          {renewCancelText(description)}
        </div>
      </div>
      <SubscribeForm
        buttonText="Subscribe"
        presentedOfferCodes={presentedOffers.map((offer) => offer.code)}
        selectedOffer={offer}
        isOfferSelected={isOfferSelected}
      />
    </div>
  );
};

export default VerticalOffer;
